// auth.module.js
import AuthService from "../services/auth.service";

const user = JSON.parse(localStorage.getItem("user"));

const state = {
  permissions: [],
  settings: [],
  status: { loggedIn: Boolean(user) },
  user: user || null,
};

const getters = {
  userPermissions: (state) => state.permissions,
};

const actions = {
  async initializeApp({ state, dispatch }) {
    try {
      if (state.status.loggedIn && state.user) {
        if (!state.user.expires_at) {
          await dispatch("checkToken");
        }

        await dispatch("permissions", state.user);

        const email = state.user?.user?.email;
        if (email) {
          dispatch("identifyUserWithHubSpot", email);
        }
      }

      await dispatch("siteSettings");
    } catch (error) {
      console.error("App initialization error:", error);
    }
  },

  async login({ commit, dispatch }, credentials) {
    try {
      const response = await AuthService.login(credentials);

      commit("loginSuccess", response);

      if (response?.user?.email) {
        dispatch("identifyUserWithHubSpot", response?.user?.email);
      }

      return response;
    } catch (error) {
      commit("loginFailure");
      throw error;
    }
  },

  async loginAsUser({ commit, dispatch }, userData) {
    try {
      const response = await AuthService.loginAsUser(userData);

      commit("loginSuccess", response);

      if (response?.user?.email) {
        // Reset HubSpot identity
        dispatch("resetHubSpotIdentity");

        // Applied HubSpot identity
        dispatch("identifyUserWithHubSpot",  response?.user?.email);
      }

      return response;
    } catch (error) {
      commit("loginFailure");
      throw error;
    }
  },

  async logout({ commit, dispatch }, user) {
    try {
      const response = await AuthService.logout(user);

      // Reset HubSpot identity
      dispatch("resetHubSpotIdentity");

      commit("logoutSuccess");

      return response;
    } catch (error) {
      commit("logoutFailure");
      throw error;
    }
  },

  async register({ commit }, userData) {
    try {
      const response = await AuthService.register(userData);
      commit("registerSuccess");
      return response;
    } catch (error) {
      commit("registerFailure");
      throw error;
    }
  },

  forgot(_, user) {
    return AuthService.forgot(user);
  },

  async permissions({ commit }, user) {
    try {
      const response = await AuthService.permissions(user);
      const permissions = response.permissions;
      commit("setPermissions", permissions);
      return response;
    } catch (error) {
      console.error("Permission fetching error:", error);
      throw error;
    }
  },

  async checkToken({ state, commit }) {
    try {
      const response = await AuthService.checkToken();

      if (response?.expires_at) {
        const updatedUser = {
          ...state.user,
          expires_at: response.expires_at,
        };

        commit("updateUser", updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      return response;
    } catch (error) {
      console.error("Token check error:", error);
      throw error;
    }
  },

  async siteSettings({ commit }) {
    try {
      const response = await AuthService.siteSettings();
      const settings = response.settings;
      commit("setSiteSettings", settings);
      return response;
    } catch (error) {
      console.error("Settings fetching error:", error);
      throw error;
    }
  },

  resetHubSpotIdentity() {
    const platform = process.env.VUE_APP_PLATFORM;

    if (platform === "production") {
      const cookies = ["hubspotutk", "__hstc", "__hssc", "__hssrc"];

      cookies.forEach((cookie) => {
        document.cookie = `${cookie}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
      });

    }
  },

  identifyUserWithHubSpot(_, email) {
    const platform = process.env.VUE_APP_PLATFORM;

    if (!email || platform !== "production") return;

    window._hsq = window._hsq || [];

    window._hsq.push([
      "identify",
      {
        email: email,
      },
    ]);

  },
};

const mutations = {
  loginSuccess(state, user) {
    state.status.loggedIn = true;
    state.user = user;
  },

  loginFailure(state) {
    state.status.loggedIn = false;
    state.user = null;
  },

  logoutSuccess(state) {
    state.status.loggedIn = false;
    state.user = null;
    state.permissions = [];
  },

  logoutFailure(state) {
    state.status.loggedIn = false;
    state.user = null;
    state.permissions = [];
  },

  registerSuccess(state) {
    state.status.loggedIn = true;
    state.user = user;
  },

  registerFailure(state) {
    state.status.loggedIn = false;
  },

  setPermissions(state, permissions) {
    state.permissions = permissions;
  },

  setSiteSettings(state, settings) {
    state.settings = settings;
  },

  updateUser(state, user) {
    state.user = user;
  },
};

export const auth = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};